/**
 * LinkCardGrid — shared card-grid primitive for bottom-of-page link sections.
 *
 * Mirrors the blog "Keep Reading" card layout and extends it with two new
 * category colours (COMPARE, RANKING) so comparison / filter / firm-ranking
 * links can live in the same visual system.
 *
 * Render is pure / synchronous → prerender-safe (all anchors appear in the
 * initial HTML Playwright captures).
 *
 * Example:
 *   <LinkCardGrid
 *     title="Compare E8 Markets"
 *     subtitle="How E8 stacks up against rival prop firms"
 *     testid="review-compare-grid"
 *     cards={[
 *       { href: "/compare/e8-markets-vs-topstep", category: "compare",
 *         title: "E8 Markets vs Topstep", descriptor: "EOD Dynamic vs intraday trailing",
 *         meta: "head-to-head" },
 *       ...
 *     ]}
 *   />
 */
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";

const CATEGORY_STYLES = {
  compare:  { label: "Compare",  bar: "from-blue-500 to-blue-400",     pill: "bg-blue-500/15 text-blue-400" },
  ranking:  { label: "Ranking",  bar: "from-amber-500 to-amber-400",   pill: "bg-amber-500/15 text-amber-400" },
  review:   { label: "Review",   bar: "from-emerald-500 to-emerald-400", pill: "bg-emerald-500/15 text-emerald-400" },
  guide:    { label: "Guide",    bar: "from-violet-500 to-violet-400", pill: "bg-violet-500/15 text-violet-400" },
  strategy: { label: "Strategy", bar: "from-orange-500 to-orange-400", pill: "bg-orange-500/15 text-orange-400" },
  ask:      { label: "Ask",      bar: "from-cyan-500 to-cyan-400",     pill: "bg-cyan-500/15 text-cyan-400" },
};

const LinkCard = ({ href, category, title, descriptor, meta, index }) => {
  const style = CATEGORY_STYLES[category] || CATEGORY_STYLES.guide;
  const isExternal = href?.startsWith("http");
  const Wrapper = isExternal ? "a" : Link;
  const wrapperProps = isExternal
    ? { href, target: "_blank", rel: "noopener noreferrer" }
    : { to: href };

  return (
    <Wrapper
      {...wrapperProps}
      data-testid={`link-card-${index}`}
      className="group relative block overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-blue-400/40 hover:shadow-lg dark:border-white/[0.06] dark:bg-[#0b1220] dark:hover:border-blue-400/35"
    >
      <div className={`absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r ${style.bar}`} />
      <div className="mb-3 flex items-start justify-between gap-3">
        <span className={`inline-flex items-center rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${style.pill}`}>
          {style.label}
        </span>
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-slate-50 transition-colors group-hover:border-blue-400/50 group-hover:bg-blue-500/10 dark:border-white/[0.06] dark:bg-white/[0.04]">
          <ArrowRight size={13} className="text-slate-400 transition-transform group-hover:translate-x-0.5 dark:text-slate-500" />
        </span>
      </div>
      <h4 className="mb-1.5 text-base font-semibold leading-snug text-slate-900 dark:text-white">{title}</h4>
      {descriptor && (
        <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-400">{descriptor}</p>
      )}
      {meta && (
        <div className="mt-4 flex items-center gap-1.5 border-t border-slate-100 pt-3 text-[11px] text-slate-500 dark:border-white/[0.05]">
          <Clock size={11} />
          {meta}
        </div>
      )}
    </Wrapper>
  );
};

export const LinkCardGrid = ({
  title,
  subtitle,
  icon: Icon,
  iconColor = "text-slate-400",
  cards = [],
  cols = 3,
  testid,
  seeAllHref,
  seeAllLabel = "View all",
}) => {
  if (!cards || cards.length === 0) return null;
  const gridCols =
    cols === 4 ? "md:grid-cols-2 lg:grid-cols-4" :
    cols === 2 ? "md:grid-cols-2" :
                 "md:grid-cols-2 lg:grid-cols-3";

  return (
    <section className="mb-10" data-testid={testid || "link-card-grid"}>
      <div className="mb-5 flex items-end justify-between gap-4">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
            {Icon && <Icon size={18} className={iconColor} />}
            {title}
          </h2>
          {subtitle && (
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
          )}
        </div>
        {seeAllHref && (
          <Link
            to={seeAllHref}
            data-testid={`${testid || "link-card-grid"}-see-all`}
            className="hidden whitespace-nowrap rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-700 dark:border-white/[0.08] dark:text-slate-400 dark:hover:border-white/[0.15] dark:hover:text-slate-200 md:inline-block"
          >
            {seeAllLabel} <ArrowRight size={12} className="ml-0.5 inline" />
          </Link>
        )}
      </div>
      <div className={`grid gap-4 ${gridCols}`}>
        {cards.map((card, i) => (
          <LinkCard key={card.href || i} index={i} {...card} />
        ))}
      </div>
    </section>
  );
};

export default LinkCardGrid;
